/**
 * CheckResultView.jsx — the single-check-result renderer shared by
 * LaunchPage.jsx (grouped by criterion, under CriterionCard) and
 * BuildValidationPanel.jsx (grouped by work-order validation, under
 * ValidationForensics). Both surfaces receive the identical `runSuite`
 * result shape pass-through, no adaptation:
 *   { checkId, status, matched, found, failures[{row,got,expected}],
 *     rows[{requestType,url,id,requestId,ts,read,ok}], rowsTruncated,
 *     failureSamples[{ts,requestType,host,iu,path,got,expected,requestId}],
 *     patterns[{dimension,value,failed,total,kind,note}], quarantined, note }
 * (conformance/runSuite.js + analyzeFailures on the proxy side.)
 *
 * This component is the UNION of what the two previous copies rendered —
 * LaunchPage's CheckResultBlock/CompactEvidenceTable (status chip,
 * matched/found counts, quarantine chip, vacuous-pass warning, an
 * expandable CheckSpecPanel + evidence table) and BuildValidationPanel's
 * FailureForensics (failure-patterns alert + failing-samples table). It does
 * NOT decide which of those a caller wants — each section is an explicit
 * boolean prop, defaulted OFF unless a section was part of that caller's
 * existing display, so reusing this component changes no pixels on either
 * page. There is deliberately no `scope`/`mode` prop: enabling a new section
 * for a surface that does not show it today is a follow-up product decision,
 * not something this refactor should do silently.
 *
 *   - `showSummary` (LaunchPage): status chip + matched/found counts +
 *     quarantine chip + note + the "passed without reading a value" warning.
 *   - `showEvidence` (LaunchPage): an expand/collapse toggle revealing
 *     CheckSpecPanel (when `def` is supplied) and the clickable evidence
 *     table over `result.rows`.
 *   - `showForensics` (BuildValidationPanel): the failure-patterns alert +
 *     failing-samples table over `result.patterns` / `result.failureSamples`.
 *     No-ops when both are empty, exactly as FailureForensics always did.
 */
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';

import CheckSpecPanel from './launch/CheckSpecPanel';
import QuarantineChip from './QuarantineChip';
import { isAbsenceCheck } from '../utils/describeCheck';
import { evidenceLine } from '../utils/evidenceLine';

// ── status colour/label maps ────────────────────────────────────────────
// CHECK_STATUS_COLOR/LABEL drive the small per-check status chip below —
// MUI theme-relative colour NAMES ('success'/'warning'/'error'/…) fed to
// Chip's `color` prop, so the chip follows the app's light/dark theme.
//
// VERDICT_COLOR drives BuildValidationPanel's SignOffCard job-level verdict
// badge — fixed brand HEX values applied via `sx.bgcolor` with white text,
// not MUI theme names. That badge is a distinct, larger visual identity for
// a job's overall pass/pass_with_review/fail/incomplete verdict, unrelated
// to any single check's result. The two maps share overlapping key
// vocabulary (pass/warn/fail/n/a) but are genuinely different palettes in
// different colour systems (theme keyword vs. literal hex) — they are kept
// here as two separate exports rather than collapsed into one map, per
// docs/... (see the "Do NOT" note in the task that produced this file).
export const CHECK_STATUS_COLOR = { pass: 'success', warn: 'warning', fail: 'error', info: 'info', 'n/a': 'default', na: 'default' };
export const CHECK_STATUS_LABEL = { pass: 'Pass', warn: 'Warn', fail: 'Fail', info: 'Info', 'n/a': 'N/A', na: 'N/A' };
export const VERDICT_COLOR = { fail: '#A32D2D', warn: '#854F0B', pass: '#1D9E75', 'n/a': '#64748B' };

function CheckStatusChip({ status }) {
  const s = status || 'n/a';
  return <Chip size="small" label={CHECK_STATUS_LABEL[s] || s} color={CHECK_STATUS_COLOR[s] || 'default'} sx={{ fontWeight: 600 }} />;
}

// HH:MM:SS from a failure sample's `ts` — tolerant of an ISO string or a ms
// number (mirrors MainApp.js's ts() tolerance). Never throws; '' on
// anything unparsable.
function formatSampleTime(ts) {
  try {
    const n = typeof ts === 'number' ? ts : Date.parse(ts);
    if (!Number.isFinite(n)) return '';
    const d = new Date(n);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-US', { hour12: false });
  } catch {
    return '';
  }
}

// ─────────────────────────────────────────────────────────────────────────
// EvidenceTable — small clickable evidence table over `result.rows[]`
// ({ requestType, url, id, requestId, ts, read, ok }). A row with
// `requestId` jumps to that request via onJumpToRequest. Formerly
// LaunchPage.jsx's CompactEvidenceTable.
// ─────────────────────────────────────────────────────────────────────────
function EvidenceTable({ rows, onJumpToRequest, cap = 8, absence = false }) {
  const list = Array.isArray(rows) ? rows.slice(0, cap) : [];
  if (!list.length) return null;
  return (
    <TableContainer sx={{ mt: 0.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 24 }} />
            <TableCell sx={{ width: 100 }}>Type</TableCell>
            <TableCell>Request</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {absence ? 'Offending value' : 'Value read'}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((r, i) => {
            const clickable = !!r?.requestId;
            const url = r?.url || '';
            const shortUrl = url.length > 60 ? `${url.slice(0, 60)}…` : url;
            return (
              <TableRow
                key={r?.id || `${r?.ts ?? ''}-${i}`}
                hover={clickable}
                onClick={clickable ? () => onJumpToRequest?.(r.requestId) : undefined}
                title={clickable ? 'Jump to this request in the dashboard' : undefined}
                sx={clickable ? { cursor: 'pointer' } : undefined}
              >
                <TableCell sx={{ width: 24, textAlign: 'center' }}>
                  <span style={{ fontWeight: 700, color: r?.ok ? '#2e7d32' : '#d32f2f' }}>{r?.ok ? '✓' : '✗'}</span>
                </TableCell>
                <TableCell sx={{ width: 100 }}>
                  {r?.requestType ? <Chip size="small" label={r.requestType} variant="outlined" sx={{ fontFamily: 'monospace' }} /> : '—'}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" title={url || undefined} sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                    {shortUrl || '—'}{clickable && <span style={{ marginLeft: 4, opacity: 0.6 }}>↗</span>}
                  </Typography>
                </TableCell>
                <TableCell>
                  {/* r.read is the value the assert actually READ off this row.
                      null means nothing was there to read — which is a PASS for
                      an absence check ("no required-severity findings") and a
                      gap worth seeing for every other kind. */}
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: 11, ...(r?.read == null ? { opacity: 0.6 } : null) }}
                    title={r?.read == null
                      ? (absence
                        ? 'Nothing matched — this is what passing looks like for an absence check'
                        : 'This request carried no value for the parameter the check reads')
                      : undefined}
                  >
                    {r?.read ?? <em>{absence ? 'none — clean' : 'no value on this request'}</em>}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {Array.isArray(rows) && rows.length > cap && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1 }}>
          showing {cap} of {rows.length}
        </Typography>
      )}
    </TableContainer>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// FailureForensicsSection — the "which requests failed + what pattern"
// callout for one failing/warning check result. Renders nothing unless the
// result carries `patterns` and/or `failureSamples` (older cached responses
// lack both — everything here is optional-chained so those degrade to
// no-op). Formerly BuildValidationPanel.jsx's FailureForensics.
//
// 1. Pattern callouts — a single Alert (severity warning) with up to 3
//    pattern notes joined by ' · ' (the aha-line an AdOps user scans first).
// 2. Failing-requests table — Time/Type/Host/IU/Got vs Expected, capped at
//    what the server already sends (≤10); a caption when the true failure
//    count exceeds the sample length.
// ─────────────────────────────────────────────────────────────────────────
function FailureForensicsSection({ result, onJumpToRequest }) {
  const patterns = Array.isArray(result?.patterns) ? result.patterns : [];
  const samples = Array.isArray(result?.failureSamples) ? result.failureSamples : [];
  if (!patterns.length && !samples.length) return null;

  const totalFailed = Array.isArray(result?.failures) ? result.failures.length : undefined;
  const truncated = typeof totalFailed === 'number' && totalFailed > samples.length;

  function jumpToSample(s) {
    if (!s?.requestId) return;
    onJumpToRequest?.(s.requestId);
  }

  return (
    <Box sx={{ mt: 1 }}>
      {patterns.length > 0 && (
        <Alert severity="warning" icon={false} sx={{ mb: samples.length ? 1 : 0, py: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Pattern: {patterns.slice(0, 3).map((p) => p?.note).filter(Boolean).join(' · ')}
          </Typography>
        </Alert>
      )}

      {samples.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Time</TableCell><TableCell>Type</TableCell><TableCell>Host</TableCell><TableCell>IU</TableCell><TableCell>Got vs expected</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {samples.map((s, i) => {
                const clickable = !!s?.requestId;
                return (
                  <TableRow
                    key={i}
                    hover={clickable}
                    onClick={clickable ? () => jumpToSample(s) : undefined}
                    title={clickable ? 'Jump to this request in the dashboard' : undefined}
                    sx={clickable ? { cursor: 'pointer' } : undefined}
                  >
                    <TableCell sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{formatSampleTime(s?.ts)}</TableCell>
                    <TableCell>{s?.requestType || '—'}</TableCell>
                    <TableCell>{s?.host || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, Menlo, monospace', maxWidth: 240, wordBreak: 'break-word' }}>
                      {s?.iu || s?.path || '—'}
                      {clickable && <span style={{ marginLeft: 4, opacity: 0.6 }}>↗</span>}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11 }}>
                      got {JSON.stringify(s?.got ?? '')} · expected {JSON.stringify(s?.expected ?? '')}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {truncated && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1 }}>
              showing first {samples.length} of {totalFailed}
            </Typography>
          )}
        </TableContainer>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CheckResultView — one check result. See file header for the section
// props and why each defaults off.
// ─────────────────────────────────────────────────────────────────────────
export default function CheckResultView({
  checkId,
  result,
  def = null,
  onSaveCheck,
  onJumpToRequest,
  showSummary = false,
  showEvidence = false,
  showForensics = false,
  cap = 8,
}) {
  const [open, setOpen] = useState(false);
  const absence = isAbsenceCheck(def);
  const hasEvidence = showEvidence && Array.isArray(result?.rows) && result.rows.length > 0;
  // Expandable whenever there is EITHER evidence or a definition to show —
  // the definition is the point: "Met" is not auditable without it.
  const expandable = showEvidence && (hasEvidence || !!def);

  return (
    <>
      {showSummary && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {expandable && (
            <IconButton size="small" onClick={() => setOpen((o) => !o)} aria-label={open ? 'collapse' : 'expand'}>
              <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'none' }}>▸</span>
            </IconButton>
          )}
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{checkId}</Typography>
          {result ? <CheckStatusChip status={result.status} /> : <Chip size="small" label="not run" variant="outlined" />}
          {result?.quarantined === true && <QuarantineChip note={result?.note} />}
          {result && typeof result.matched === 'number' && (
            <Typography
              variant="caption"
              color="text.secondary"
              title={absence
                ? 'matched = requests this check graded. "clean on N" = requests carrying nothing that would fail it.'
                : 'matched = requests this check graded. "read a value on N" = requests that actually carried the parameter it reads — a low number next to a Pass means the check mostly had nothing to look at.'}
            >
              {(result.failures?.length ?? 0) === 0 ? result.matched : result.matched - result.failures.length}/{result.matched} matched
              {typeof result.found === 'number'
                ? (absence
                  ? ` · clean on ${result.matched - result.found}`
                  : ` · read a value on ${result.found}`)
                : ''}
            </Typography>
          )}
          {result?.note && <Typography variant="caption" color="text.secondary">{result.note}</Typography>}
        </Stack>
      )}
      {/* The evidence line: what was looked at, which value was read from where,
          how many readable requests held, one example (utils/evidenceLine.js; same
          sentence as the downloadable report and the proxy Markdown report).
          Not-run, missing-definition and request-only (logcat) cards say so here. */}
      {showSummary && (
        <Typography variant="caption" color="text.secondary" data-testid="evidence-line" sx={{ display: 'block', pl: 1, mt: 0.25 }}>
          {evidenceLine(def, result ?? null)}
        </Typography>
      )}
      {/* The vacuous-pass warning: a non-absence check that passed without
          reading a single value proved nothing about this session. */}
      {showSummary && !absence && def && result && result.status === 'pass'
        && typeof result.found === 'number' && result.found === 0 && (result.matched || 0) > 0 && (
        <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', pl: 1 }}>
          Passed without reading a value on any of the {result.matched} requests it graded — treat as unproven, not verified.
        </Typography>
      )}
      {expandable && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <CheckSpecPanel check={def} onSave={onSaveCheck ? (next) => onSaveCheck(next) : undefined} />
          {hasEvidence && <EvidenceTable rows={result.rows} onJumpToRequest={onJumpToRequest} cap={cap} absence={absence} />}
        </Collapse>
      )}
      {showForensics && <FailureForensicsSection result={result} onJumpToRequest={onJumpToRequest} />}
    </>
  );
}
